import { ArrowDropDown, Check } from "@mui/icons-material";
import { Box, Button, Fade, Popper, Skeleton, SxProps, TextField, Theme } from "@mui/material";
import React, { Fragment, useRef, useState } from "react";
import theme from "theme";
import { Link, useLocation } from "react-router-dom";
import { groupBy } from "helpers/coll";

export interface BreadcrumbDropdownPickerOption {
  title: string;
  subtitleContent?: React.ReactNode;
  icon?: React.ReactNode;
  route: string;
  searchParamsString?: string;
  category?: string;
  uniqueId: string;
}

type Props = {
  search: boolean;
  searchPlaceholder?: string;
  options: BreadcrumbDropdownPickerOption[];
  loading?: boolean;
  label: string;
  noResultsText: string | undefined;
  onChange?: (value: string) => void;
  currentRoute: string;
  pendoId?: string;
  sx?: SxProps<Theme>;
  PopoverSX?: SxProps<Theme>;
};

export default function BreadcrumbDropdownPicker({
  search,
  searchPlaceholder,
  options,
  loading,
  label,
  noResultsText: noResultsTextProp,
  onChange,
  currentRoute,
  pendoId,
  sx,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const anchorRef = React.useRef<HTMLAnchorElement | null>(null);
  const dropdownIsHovered = React.useRef(false);
  const letMeOut = React.useRef(false);

  // timeout is a timeout or null
  const hoverTimeout = useRef<number | null>(null);

  const location = useLocation();

  const noResultsText = noResultsTextProp ?? "No results found";

  const clearSearch = () => {
    setSearchQuery("");
    setPickerOpen(false);
    onChange && onChange("");
  };

  const showDropdown = search || options.length > 1;

  const canBeOpen = pickerOpen && Boolean(anchorRef.current);
  const id = canBeOpen ? "transition-popper" : undefined;

  const groupedOptions = groupBy(options, (option) => option.category);

  return (
    <>
      <Box sx={sx}>
        <Button
          component="a"
          aria-describedby={id}
          data-pendo={pendoId}
          href={currentRoute}
          ref={anchorRef}
          onMouseEnter={() => {
            if (showDropdown && !letMeOut.current) {
              hoverTimeout.current = window.setTimeout(() => {
                setPickerOpen(true);
              }, 200);
            }
          }}
          onMouseLeave={() => {
            letMeOut.current = false;
            setTimeout(() => {
              if (!dropdownIsHovered.current) {
                setPickerOpen(false);
                clearSearch();
              }
            }, 10);
            if (hoverTimeout !== null) {
              if (hoverTimeout.current !== null) clearTimeout(hoverTimeout.current);
              hoverTimeout.current = null;
            }
          }}
          onClick={() => {
            // navigate to the current route if it's not the current route
            if (currentRoute !== location.pathname) {
              letMeOut.current = true;
              setPickerOpen(false);
            }
          }}
          className="normal-case flex items-center gap-2">
          {options.find((option) => option.route === currentRoute)?.icon}
          <p className="text-xs font-bold text-peri-900">{label}</p>
          {showDropdown && (
            <ArrowDropDown
              sx={{
                transform: !pickerOpen ? "rotate(-90deg)" : "rotate(0deg)",
                transition: "transform 0.1s",
                ml: 1,
                color: theme.palette.grey[300],
              }}
            />
          )}
        </Button>
      </Box>

      <Popper
        id={id}
        placement="bottom-start"
        open={pickerOpen}
        anchorEl={anchorRef.current}
        onMouseEnter={() => {
          dropdownIsHovered.current = true;
        }}
        onMouseLeave={() => {
          dropdownIsHovered.current = false;
          setPickerOpen(false);
          clearSearch();
        }}
        sx={{
          zIndex: theme.zIndex.appBar,
        }}
        transition>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={100}>
            <Box
              sx={{
                width: search ? 400 : undefined,
                p: 0,
                boxShadow: 1,
                borderRadius: 1,
                backgroundColor: "white",
              }}>
              <Box
                sx={{
                  display: search ? "block" : "none",
                  p: 1,
                  borderBottom: "1px solid",
                  borderColor: theme.palette.grey["100"],
                }}>
                <TextField
                  fullWidth
                  value={searchQuery}
                  placeholder={searchPlaceholder}
                  onChange={(v) => {
                    setSearchQuery(v.target.value);
                    onChange && onChange(v.target.value);
                  }}
                  inputRef={(input) => input && input.focus()}
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                />
              </Box>

              <div className="overflow-auto max-h-[300px] flex flex-col gap-1 w-full options-container">
                {loading &&
                  [1, 2, 3].map((i) => (
                    <Skeleton
                      sx={{
                        height: 57,
                      }}
                      key={i}
                      variant="rectangular"
                      animation="wave"
                    />
                  ))}
                {!loading && options.length === 0 && (
                  <div className="p-4">
                    <p className="text-sm text-gray-500">{noResultsText}</p>
                  </div>
                )}
                {Array.from(groupedOptions).map(([category, options], idx) => (
                  <Fragment key={`options-grouped-${idx}`}>
                    {category !== undefined && <p className="mx-2 mt-2 text-gray-200 text-sm">{category}</p>}
                    {!loading &&
                      options.map((option, idx) => {
                        return (
                          <Link
                            id={`breadcrumb-dropdown-picker-${idx}`}
                            key={`${option.title}-${option.route}-${idx}`}
                            to={`${option.route + (option.searchParamsString || "")}`}
                            onClick={() => {
                              setPickerOpen(false);
                              // set the current to false
                              dropdownIsHovered.current = false;
                              clearSearch();
                            }}
                            className="p-2 px-4 w-full hover:bg-gray-50">
                            <div className="flex justify-between items-center w-full gap-2">
                              <div className="flex items-center gap-2 w-full">
                                {option.icon && <Box sx={{ fontSize: "0.5rem" }}>{option.icon}</Box>}
                                <div className="flex flex-col flex-wrap items-start w-full text-left">
                                  <p className="text-xs font-semibold">{option.title}</p>
                                  {option.subtitleContent}
                                </div>
                              </div>
                              {/* We use visibility here to keep the height of the rows consistent */}
                              <Check visibility={currentRoute === option.route ? "visible" : "hidden"} />
                            </div>
                          </Link>
                        );
                      })}
                  </Fragment>
                ))}
              </div>
            </Box>
          </Fade>
        )}
      </Popper>
    </>
  );
}
